import React, { useState, useEffect } from 'react';
import {
  Users,
  RefreshCcw,
  Activity,
  Calendar,
  Search,
  ChevronRight,
  CheckCircle2,
  ArrowUpRight
} from 'lucide-react';
import { format } from "date-fns";
import { motion } from 'framer-motion';
import useApi from '../../../hooks/useApi';
import { AttendanceDistributionChart } from '../admin-dashboard/charts/AttendanceDistributionChart';
import { CounselorPerformanceChart } from '../admin-dashboard/charts/CounselorPerformanceChart';
import { SessionTrendsChart } from '../admin-dashboard/charts/SessionTrendsChart';
import { StatCard } from '../admin-dashboard/StatCard';
import { DashboardFilters } from '../admin-dashboard/filters/DashboardFilters';
import { DashboardEventBanner } from '../../announcements';
import ReportsSection from '../ReportsSection';

const COLORS = {
  primary: "#003087",
  secondary: "#64748B",
  success: "#059669"
};

const HodDashboard = () => {
  const { get } = useApi();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(null);
  const [state, setState] = useState({
    stats: null,
    trends: [],
    counselors: [],
    attendance: [],
    loading: true,
    error: null,
    lastUpdated: null,
    globalCounselors: []
  });


  const fetchData = async (filterParams = {}) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const [stats, trends, counselors, attendance, allCounselors] = await Promise.all([
        get("/dashboard/stats/admin"),
        get("/dashboard/sessions/trends", { params: filterParams }),
        get("/dashboard/counselors/performance", { params: filterParams }),
        get("/dashboard/attendance/rates", { params: filterParams }),
        get("/users", { params: { role: 'counselor', limit: 100 } })
      ]);

      setState({
        stats: stats.data.data,
        trends: trends.data.data,
        counselors: counselors.data.data,
        attendance: attendance.data.data,
        globalCounselors: allCounselors.data.data || [],
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error("Dashboard Data Fetch Error:", error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchData(filters);
  }, [filters]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Minimal Header */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-2 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">HOD Overview</h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs font-medium">Strategic Monitoring Hub</p>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search metrics..."
                className="w-full sm:w-48 pl-8 pr-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-400 text-xs transition-all dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-1.5 rounded-md text-[10px] font-bold text-slate-500 tabular-nums">
              <span>{state.lastUpdated ? format(state.lastUpdated, "HH:mm") : "--:--"}</span>
              <button
                onClick={() => fetchData(filters)}
                className="hover:text-blue-600 transition-colors"
                disabled={state.loading}
              >
                <RefreshCcw className={`h-3 w-3 ${state.loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </header>

        {/* Event Banner */}
        <DashboardEventBanner />

        {/* Compact Stat Grid - No Left Border */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={state.stats?.totalUsers || 0}
            icon={Users}
            isLoading={state.loading}
            showBorder={false}
            className="!p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none"
          />
          <StatCard
            title="Active Counselors"
            value={state.stats?.activeCounselors || 0}
            icon={Calendar}
            isLoading={state.loading}
            showBorder={false}
            className="!p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none"
          />
          <StatCard
            title="Active Sessions"
            value={state.stats?.systemMetrics?.inProgressSessions || 0}
            icon={Activity}
            isLoading={state.loading}
            showBorder={false}
            className="!p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none"
          />
          <StatCard
            title="Completed"
            value={state.stats?.totalSessions || 0}
            icon={CheckCircle2}
            isLoading={state.loading}
            showBorder={false}
            className="!p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none"
          />
        </div>

        {/* Global Filters */}
        <div className="bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm">
          <DashboardFilters
            onFilter={setFilters}
            onExport={() => { }}
            isLoading={state.loading}
          />
        </div>

        {/* Main Analytics Grid */}
        <div className="grid grid-cols-12 gap-6">

          <div className="col-span-12 lg:col-span-8 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
              <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-800/20">
                <h2 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Engagement Trends</h2>
                <button className="text-slate-400 hover:text-slate-600 transition-colors">
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
              <div className="p-6">
                <SessionTrendsChart data={state.trends} isLoading={state.loading} />
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-2 shadow-sm">
              <CounselorPerformanceChart
                data={state.counselors}
                isLoading={state.loading}
                counselors={state.globalCounselors}
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 shadow-sm">
              <AttendanceDistributionChart data={state.attendance} isLoading={state.loading} />
            </div>

            {/* Active Counselors List */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Active Counselors</h3>
              <div className="space-y-4">
                {state.counselors.slice(0, 3).map((c, i) => (
                  <div key={i} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                        {c.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 dark:text-slate-200 text-xs">{c.name}</p>
                        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-tighter">{c.retentionRate}% Retention</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:translate-x-1 transition-transform" />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
              <ReportsSection />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HodDashboard;
